/**
 * US-642 — group dashboard background signals by job.
 *
 * Pure on purpose: no React, no network. The dashboard's New Matches section
 * arrives as up to ~90 per-signal rows that are really a handful of per-job
 * batches from the signal-detector scheduler, so the whole layout decision is
 * a reorganisation of data already on the wire. Keeping it here means the
 * ordering rules are assertable without rendering anything
 * (`docs/WORKING_RULES.md` §3).
 */

export interface GroupableSignal {
  id: number;
  candidateId: number;
  candidateName: string | null;
  jobId: number;
  jobTitle: string | null;
  payload?: Record<string, unknown> | null;
  createdAt?: string;
}

export interface SignalJobGroup {
  /** Stable React key. `job-<id>` for a real job, `unknown` for the null bucket. */
  key: string;
  /** null for the Unknown job bucket — those signals have no resolvable job row. */
  jobId: number | null;
  jobTitle: string;
  count: number;
  /** Modal star value across the group, or null when no signal carries one. */
  stars: number | null;
  /**
   * The first `NAMES_PER_CARD` signals of the group, in arrival order — the
   * exact rows the card renders.
   *
   * This is deliberately the SIGNALS, not their names: the component used to
   * re-derive its own per-card list with `s.jobId === group.jobId`, a DIFFERENT
   * predicate from this module's (which buckets `jobTitle == null` first). A
   * signal with a jobId whose title is null rendered on both the titled card
   * and the Unknown card, while the count badge — read from here — disagreed
   * with the names beside it. One derivation, asserted in one place.
   */
  visibleSignals: GroupableSignal[];
  /** Most recent `createdAt` in the group; drives the ordering tie-break. */
  latestCreatedAt: string | null;
  /** Existing route, or null when the group must not link (Unknown job). */
  linkTo: string | null;
}

export const UNKNOWN_JOB_KEY = 'unknown';
export const UNKNOWN_JOB_TITLE = 'Unknown job';

/** How many candidate names a card shows before the "+N" remainder. */
export const NAMES_PER_CARD = 3;

interface Bucket {
  key: string;
  jobId: number | null;
  jobTitle: string;
  signals: GroupableSignal[];
}

function modalStars(signals: GroupableSignal[]): number | null {
  const tally = new Map<number, number>();
  for (const s of signals) {
    const raw = s.payload?.stars;
    if (typeof raw === 'number' && Number.isFinite(raw)) {
      tally.set(raw, (tally.get(raw) ?? 0) + 1);
    }
  }
  if (tally.size === 0) return null;

  let best: number | null = null;
  let bestCount = -1;
  for (const [stars, n] of tally) {
    // Ties resolve to the higher star value so a card never under-sells a
    // group that is genuinely split between two bands.
    if (n > bestCount || (n === bestCount && best !== null && stars > best)) {
      best = stars;
      bestCount = n;
    }
  }
  return best;
}

function latestCreatedAt(signals: GroupableSignal[]): string | null {
  let latest: string | null = null;
  for (const s of signals) {
    if (typeof s.createdAt !== 'string' || s.createdAt.length === 0) continue;
    if (latest === null || s.createdAt > latest) latest = s.createdAt;
  }
  return latest;
}

/**
 * Group by job, ordered by descending count, tie-broken by the most recent
 * `createdAt` in the group and finally by jobId — so ordering is TOTAL and does
 * not reshuffle when the same data is refetched in a different row order.
 *
 * `createdAt DESC` alone was rejected explicitly: it lets a 1-signal job
 * outrank a 20-signal one, which is the defect the grouping exists to fix.
 */
export function groupSignalsByJob(signals: GroupableSignal[]): SignalJobGroup[] {
  const buckets = new Map<string, Bucket>();

  for (const s of signals) {
    // The signals route leftJoins jobs, so a null title is reachable in
    // production. Those rows share ONE bucket and never produce a link.
    const isUnknown = s.jobTitle === null || s.jobTitle === undefined;
    const key = isUnknown ? UNKNOWN_JOB_KEY : `job-${s.jobId}`;
    let bucket = buckets.get(key);
    if (!bucket) {
      bucket = {
        key,
        jobId: isUnknown ? null : s.jobId,
        jobTitle: isUnknown ? UNKNOWN_JOB_TITLE : (s.jobTitle as string),
        signals: [],
      };
      buckets.set(key, bucket);
    }
    bucket.signals.push(s);
  }

  const groups: SignalJobGroup[] = [...buckets.values()].map((b) => ({
    key: b.key,
    jobId: b.jobId,
    jobTitle: b.jobTitle,
    count: b.signals.length,
    stars: modalStars(b.signals),
    visibleSignals: b.signals.slice(0, NAMES_PER_CARD),
    latestCreatedAt: latestCreatedAt(b.signals),
    linkTo: b.jobId === null ? null : `/jobs/${b.jobId}/sourcing`,
  }));

  return groups.sort(compareSignalGroups);
}

/**
 * Total order over groups. Exported so the mutation proof and the ordering
 * tests can target exactly one function.
 */
export function compareSignalGroups(a: SignalJobGroup, b: SignalJobGroup): number {
  if (a.count !== b.count) return b.count - a.count;

  const at = a.latestCreatedAt ?? '';
  const bt = b.latestCreatedAt ?? '';
  if (at !== bt) return at < bt ? 1 : -1;

  // Final discriminator: without this, two groups with equal counts and equal
  // timestamps would keep whatever order the fetch happened to return.
  const aid = a.jobId ?? Number.MAX_SAFE_INTEGER;
  const bid = b.jobId ?? Number.MAX_SAFE_INTEGER;
  return aid - bid;
}
